/** Export API functions. */

import { apiGet, apiPost } from '@/api/client';
import type { ExportJobResponse, ExportStatusResponse } from '@/api/types';

export function triggerExport(
  mapId: string,
  format: string,
): Promise<ExportJobResponse> {
  return apiPost<ExportJobResponse>(`/maps/${mapId}/export`, {
    format,
  });
}

export function getExportStatus(jobId: string): Promise<ExportStatusResponse> {
  return apiGet<ExportStatusResponse>(`/exports/${jobId}`);
}

/**
 * Poll the export job until it reaches a terminal state.
 * Returns the download URL on success.
 * Throws if the job fails or times out (max 60 polls).
 */
export async function pollExportUntilComplete(
  jobId: string,
  intervalMs = 2000,
): Promise<string> {
  const maxPolls = 60;
  for (let i = 0; i < maxPolls; i++) {
    const status = await getExportStatus(jobId);

    if (status.status === 'complete') {
      if (!status.downloadUrl) {
        throw new Error('Export completed but no download URL was provided');
      }
      return status.downloadUrl;
    }

    if (status.status === 'failed') {
      throw new Error(`Export job ${jobId} failed`);
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new Error(`Export job ${jobId} timed out after ${maxPolls} polls`);
}
